import React, { useEffect } from 'react';
import { ExternalLink } from 'lucide-react';

const Tutorials = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tutorials = [
    {
      id: 'google-workspace',
      category: 'Google Workspace with AI',
      items: [
        {
          title: 'Create Google Forms with Gemini',
          description: 'Learn how to automatically generate surveys, attendance forms, and RSVPs using AI',
          url: 'https://support.google.com/docs/answer/15259015?hl=en',
          difficulty: 'Beginner'
        },
        {
          title: 'Use Gemini for Google Calendar',
          description: 'Manage your schedule by speaking to Gemini - add events, set reminders, and organize your time',
          url: 'https://www.youtube.com/watch?v=MIa_282QWMA',
          difficulty: 'Beginner'
        },
        {
          title: 'Use Gemini for Google Sheets',
          description: 'Create and edit spreadsheets with AI for club management, grade tracking, and data analysis',
          url: 'https://www.youtube.com/watch?v=QiRUQBwA4EA',
          difficulty: 'Beginner'
        }
      ]
    },
    {
      id: 'studying-learning',
      category: 'Studying & Learning',
      items: [
        {
          title: 'ChatGPT for Students Guide',
          description: 'Complete guide on using ChatGPT for homework help, studying, and research',
          url: 'https://www.youtube.com/watch?v=VznoKyh6AXs',
          difficulty: 'Beginner'
        },
        {
          title: 'Using Quizlet AI Study Tools',
          description: 'Generate flashcards, practice tests, and study guides automatically from your notes',
          url: 'https://quizlet.com/labs',
          difficulty: 'Beginner'
        },
        {
          title: 'NotebookLM for Study Notes',
          description: 'Upload your class materials and get AI-generated study guides, summaries, and Q&A',
          url: 'https://notebooklm.google.com/',
          difficulty: 'Intermediate'
        },
        {
          title: 'Khan Academy AI Tutor (Khanmigo)',
          description: 'Free AI tutor that helps with math, science, and more without giving away answers',
          url: 'https://www.khanacademy.org/khanmigo',
          difficulty: 'Beginner'
        }
      ]
    },
    {
      id: 'content-creation',
      category: 'Content Creation',
      items: [
        {
          title: 'Design with Canva AI',
          description: 'Create social media posts, flyers, and graphics for your clubs and projects',
          url: 'https://www.canva.com/ai-image-generator/',
          difficulty: 'Beginner'
        },
        {
          title: 'Build a Website with AI',
          description: 'Create a website for your club, project, or portfolio using AI builders',
          url: 'https://www.wix.com/blog/how-to-use-ai-website-builder',
          difficulty: 'Intermediate'
        },
        {
          title: 'Video Editing with AI',
          description: 'Use AI to edit videos, add captions, and create content for social media',
          url: 'https://www.capcut.com/',
          difficulty: 'Intermediate'
        },
        {
          title: 'Generate Presentation Slides',
          description: 'Create entire presentations from prompts using Gamma or similar tools',
          url: 'https://gamma.app/',
          difficulty: 'Beginner'
        }
      ]
    },
    {
      id: 'research-writing',
      category: 'Research & Writing',
      items: [
        {
          title: 'Perplexity AI for Research',
          description: 'Get cited answers for research papers with sources automatically included',
          url: 'https://www.perplexity.ai/',
          difficulty: 'Beginner'
        },
        {
          title: 'Grammarly AI Writing Assistant',
          description: 'Improve your writing with AI-powered grammar, tone, and clarity suggestions',
          url: 'https://www.grammarly.com/',
          difficulty: 'Beginner'
        },
        {
          title: 'Consensus AI for Scientific Research',
          description: 'Search through academic papers and get evidence-based answers',
          url: 'https://consensus.app/',
          difficulty: 'Intermediate'
        }
      ]
    }
  ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return '#28a745';
      case 'Intermediate':
        return '#ffc107';
      case 'Advanced':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'white',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <h1 style={{
          textAlign: 'left',
          color: '#2c3e50',
          marginBottom: '0.5rem',
          fontSize: '3rem',
          fontWeight: 'bold'
        }}>
          AI Tutorials
        </h1>
        
        <p style={{
          textAlign: 'left',
          color: '#6c757d',
          marginBottom: '3rem',
          fontSize: '1.2rem',
          maxWidth: '800px'
        }}>
          Step-by-step guides and resources to help you use AI effectively for school, extracurriculars, and college prep
        </p>

        {tutorials.map((section, sectionIndex) => (
          <div key={sectionIndex} id={section.id} style={{ marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: '#2c3e50',
              marginBottom: '1.5rem',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid #e9ecef'
            }}>
              {section.category}
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '1.5rem'
            }}>
              {section.items.map((tutorial, tutIndex) => (
                <a
                  key={tutIndex}
                  href={tutorial.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: 'none',
                    backgroundColor: '#f8f9fa',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    border: '1px solid #e9ecef',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = '#007bff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e9ecef';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '0.5rem'
                  }}>
                    <h3 style={{
                      fontSize: '1.2rem',
                      fontWeight: '600',
                      color: '#2c3e50',
                      margin: 0,
                      flex: 1
                    }}>
                      {tutorial.title}
                    </h3>
                    <ExternalLink size={18} color="#6c757d" style={{ flexShrink: 0 }} />
                  </div>
                  
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#6c757d',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    {tutorial.description}
                  </p>
                  
                  <div style={{
                    display: 'inline-block',
                    alignSelf: 'flex-start',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    backgroundColor: getDifficultyColor(tutorial.difficulty) + '15',
                    color: getDifficultyColor(tutorial.difficulty)
                  }}>
                    {tutorial.difficulty}
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}
        
        <div style={{
          marginTop: '4rem',
          padding: '2rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#2c3e50',
            marginBottom: '1rem'
          }}>
            Have a tutorial suggestion?
          </h3>
          <p style={{
            fontSize: '1rem',
            color: '#6c757d',
            margin: 0
          }}>
            If you know of a great AI tool or tutorial for students, let us know! We're always looking to expand this resource.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Tutorials;

